/**
 * Analytics Layout
 * Main layout component with tab navigation for the 3 analytics pages
 */

import React from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { BarChart3, Calendar, Shield, TrendingUp, LogOut } from 'lucide-react';
import Dashboard12Month from './Dashboard12Month';
import MonthlyDrillDown from './MonthlyDrillDown';
import IntegrityHub from './IntegrityHub';

const AnalyticsLayout = () => {
  const tabs = [
    {
      path: '',
      label: '12-Month Dashboard',
      icon: TrendingUp,
      description: 'Overview of performance trends'
    },
    {
      path: 'monthly',
      label: 'Monthly Drill-Down',
      icon: Calendar,
      description: 'Detailed monthly analysis'
    },
    {
      path: 'integrity',
      label: 'Integrity Hub',
      icon: Shield,
      description: 'Data quality and audit'
    }
  ];

  const handleLogout = () => {
    sessionStorage.removeItem('analytics_pin_verified');
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gray-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Title */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold">YYZ Prep Analytics</h1>
                <p className="text-xs text-gray-400">Robust Financial Engine</p>
              </div>
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Lock Dashboard
            </button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <NavLink
                  key={tab.path}
                  to={`/analytics/${tab.path}`}
                  end={tab.path === ''}
                  className={({ isActive }) => `
                    flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2 transition-colors
                    ${isActive
                      ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route index element={<Dashboard12Month />} />
          <Route path="monthly" element={<MonthlyDrillDown />} />
          <Route path="integrity" element={<IntegrityHub />} />
          <Route path="*" element={<Navigate to="/analytics" replace />} />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500">
            YYZ Prep Analytics Engine | Data updates in real-time
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AnalyticsLayout;
